import { useMemo, type CSSProperties, type KeyboardEvent } from 'react';
import type { Detour, EntranceId, MapPublic, PuzzleType } from '@il-colpo/engine';
import { useGame } from '../GameContext.tsx';
import { PLAN_VIEW_ID } from '../useGameKeys.ts';
import { NODE_H, NODE_W, layoutMap } from './layout.ts';
import styles from './Blueprint.module.css';

export interface BlueprintMark {
  room: string;
  kind: 'here' | 'danger' | 'wait' | 'go';
}

export interface BlueprintProps {
  map: MapPublic;
  /** Puzzle types this player knows for each room (their layer of the blueprint). */
  layers?: Record<string, PuzzleType[]>;
  entrance?: EntranceId | null;
  route?: readonly string[];
  detours?: readonly Detour[];
  /** The room the team is in during the heist. */
  current?: string | null;
  done?: ReadonlySet<string>;
  marks?: readonly BlueprintMark[];
  /** Rooms that can be clicked (plan building, pings). */
  selectable?: ReadonlySet<string>;
  onSelect?: (room: string) => void;
  /** Guarded corridors, if this role can see them. */
  guarded?: readonly [string, string][] | null;
}

function words(name: string, max = 15): string[] {
  const out: string[] = [];
  let line = '';
  for (const w of name.split(' ')) {
    if (line && (line + ' ' + w).length > max) {
      out.push(line);
      line = w;
    } else line = line ? `${line} ${w}` : w;
  }
  if (line) out.push(line);
  return out.slice(0, 3);
}

/** The blueprint: floors of rooms joined by passages, with the plan drawn over it. */
export function Blueprint({
  map,
  layers,
  entrance,
  route = [],
  detours = [],
  current = null,
  done,
  marks = [],
  selectable,
  onSelect,
  guarded,
}: BlueprintProps) {
  const { g } = useGame();
  const layout = useMemo(() => layoutMap(map), [map]);
  const inRoute = new Set(route);
  const alcoves = new Set(detours.map((d) => d.room));
  const routeEdges = new Set<string>();
  for (let i = 0; i + 1 < route.length; i++) routeEdges.add(`${route[i]}|${route[i + 1]}`);
  for (const d of detours) routeEdges.add(`${d.anchor}|${d.room}`);
  const isGuarded = (a: string, b: string): boolean =>
    guarded?.some(([x, y]) => (x === a && y === b) || (x === b && y === a)) ?? false;
  const edgeOnRoute = (a: string, b: string): boolean =>
    routeEdges.has(`${a}|${b}`) || routeEdges.has(`${b}|${a}`);
  const entranceRoom = entrance ? map.entrances[entrance] : null;

  const press = (room: string) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect?.(room);
    }
  };

  return (
    <div
      id={PLAN_VIEW_ID}
      className={styles['scroll']}
      tabIndex={0}
      role="group"
      aria-label={g(`maps.${map.id}.name`)}
    >
      <svg className={styles['map']} viewBox={`0 0 ${layout.width} ${layout.height}`}>
        {map.edges.map((e, index) => {
          const a = layout.nodes[e.a];
          const b = layout.nodes[e.b];
          if (!a || !b) return null;
          const on = edgeOnRoute(e.a, e.b);
          return (
            <line
              // The key includes the route: a wall that joins the plan is drawn in again.
              key={`${e.a}-${e.b}-${on ? 'plan' : 'wall'}`}
              pathLength={1}
              style={{ '--i': index } as CSSProperties}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              className={[
                styles['edge'],
                on ? styles['onRoute'] : '',
                isGuarded(e.a, e.b) ? styles['guarded'] : '',
              ].join(' ')}
            />
          );
        })}
        {map.ropeLinks.map(([x, y], index) => {
          const a = layout.nodes[x];
          const b = layout.nodes[y];
          if (!a || !b) return null;
          const on = edgeOnRoute(x, y);
          return (
            <path
              key={`rope-${x}-${y}-${on ? 'plan' : 'wall'}`}
              pathLength={1}
              style={{ '--i': map.edges.length + index } as CSSProperties}
              d={`M${a.x},${a.y} Q${(a.x + b.x) / 2 + 40},${(a.y + b.y) / 2} ${b.x},${b.y}`}
              className={[styles['rope'], on ? styles['onRoute'] : ''].join(' ')}
            />
          );
        })}
        {map.rooms.map((room, index) => {
          const p = layout.nodes[room.id];
          if (!p) return null;
          const name = g(`rooms.${room.id}`);
          const types = layers?.[room.id] ?? [];
          const classes = [
            styles['room'],
            room.kind === 'optional' ? styles['optional'] : '',
            room.kind === 'vault' ? styles['vault'] : '',
            inRoute.has(room.id) ? styles['picked'] : '',
            alcoves.has(room.id) ? styles['picked'] : '',
            current === room.id ? styles['current'] : '',
            done?.has(room.id) ? styles['done'] : '',
            selectable?.has(room.id) ? styles['selectable'] : '',
            entranceRoom === room.id ? styles['entrance'] : '',
          ].join(' ');
          const label = words(name);
          const interactive = selectable?.has(room.id) && onSelect;
          return (
            <g
              key={room.id}
              className={classes}
              transform={`translate(${p.x - NODE_W / 2},${p.y - NODE_H / 2})`}
              style={{ '--i': index } as CSSProperties}
              role={interactive ? 'button' : 'img'}
              tabIndex={interactive ? 0 : undefined}
              aria-label={`${name}${room.extraLoot ? ` +${room.extraLoot}` : ''}`}
              onClick={interactive ? () => onSelect(room.id) : undefined}
              onKeyDown={interactive ? press(room.id) : undefined}
            >
              <rect width={NODE_W} height={NODE_H} rx={8} />
              <text x={NODE_W / 2} y={label.length > 1 ? 16 : 24} textAnchor="middle">
                {label.map((l, i) => (
                  <tspan key={l} x={NODE_W / 2} dy={i === 0 ? 0 : 13}>
                    {l}
                  </tspan>
                ))}
              </text>
              {types.length > 0 ? (
                <text className={styles['types']} x={NODE_W / 2} y={NODE_H - 6} textAnchor="middle">
                  {types.map((tp) => g(`puzzles.${tp}.short`)).join(' · ')}
                </text>
              ) : null}
              {room.extraLoot > 0 ? (
                <text className={styles['loot']} x={NODE_W - 5} y={12} textAnchor="end">
                  {`+${room.extraLoot}`}
                </text>
              ) : null}
              {room.kind === 'vault' ? (
                <text className={styles['loot']} x={6} y={12}>
                  {'★'}
                </text>
              ) : null}
            </g>
          );
        })}
        {marks.map((m, i) => {
          const p = layout.nodes[m.room];
          if (!p) return null;
          return (
            <g
              key={`${m.room}-${i}`}
              className={[styles['ping'], styles[m.kind] ?? ''].join(' ')}
              transform={`translate(${p.x},${p.y - NODE_H / 2 - 4})`}
              aria-hidden="true"
            >
              <circle r={9} />
              <text textAnchor="middle" y={4}>
                {m.kind === 'danger' ? '!' : m.kind === 'wait' ? '…' : m.kind === 'go' ? '»' : '•'}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
